import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CustomEntityDialog } from "@/components/ui/CustomEntityDialog";
import {
  useProductSelectState,
  type ProductSelectComboProps,
} from "./useProductSelectState";
import { ProductDropdown } from "./ProductDropdown";
import { ProductDisplayValue } from "./ProductDisplayValue";
import { usePreOrderFeatureFlags } from "../../hooks/usePreOrderFeatureFlags";
import { useFeatureFlag } from "@/query/hooks/useFeatureFlags";

export type { ProductSelectComboProps };

/**
 * Shell: renders the same closed-state markup as the full combo but skips
 * the heavy `useProductSelectState` setup (18 useStates, 7 useEffects,
 * `useProducts` subscription, Fuse index, dropdown observers). Mounts in
 * the parent's commit phase at a fraction of the cost.
 *
 * Visual parity: re-uses `ProductDisplayValue` with derived (not state)
 * `currentProductDetails` + `skuInput`. Stub handlers all trigger
 * `onActivate`, which the wrapper interprets as "hydrate the full editor,
 * and if the user clicked the button, auto-open the dropdown on mount".
 */
function ProductSelectComboShell({
  value,
  product,
  className = "",
  label,
  required = false,
  disabled = false,
  isLastRow = false,
  customProduct,
  allowCustom = false,
  placeholder,
  priceLookup,
  hideClosedPricePill,
  stockLookup,
  onActivateOpen,
  onActivate,
}: ProductSelectComboProps & {
  onActivateOpen: () => void;
  onActivate: () => void;
}) {
  const currentProductDetails = product ?? null;
  const isCustomActive = !!(allowCustom && customProduct?.name && !value);
  const skuInput = currentProductDetails?.sku ?? "";
  const buttonRef = useRef<HTMLButtonElement>(null);
  const skuInputRef = useRef<HTMLInputElement>(null);

  return (
    <ProductDisplayValue
      value={value}
      disabled={disabled}
      isLastRow={isLastRow}
      isOpen={false}
      isCustomActive={isCustomActive}
      currentProductDetails={currentProductDetails}
      customProduct={customProduct}
      placeholder={placeholder}
      priceLookup={priceLookup}
      hideClosedPricePill={hideClosedPricePill}
      stockLookup={stockLookup}
      stockSummary={null}
      skuInput={skuInput}
      className={className}
      label={label}
      required={required}
      handleToggle={onActivateOpen}
      handleButtonKeyDown={onActivateOpen}
      handleKeyDown={onActivate}
      handleSkuChange={onActivate}
      handleSkuBlur={onActivate}
      handleSkuFocus={onActivate}
      setCustomDialogOpen={onActivate}
      buttonRef={buttonRef}
      skuInputRef={skuInputRef}
    />
  );
}

const ProductSelectComboInner = (
  props: ProductSelectComboProps & {
    openOnMount?: boolean;
    focusSkuOnMount?: boolean;
  },
) => {
  const {
    className = "",
    label,
    required = false,
    disabled = false,
    isLastRow = false,
    portalContainer,
    priceLookup,
    stockLookup,
    stockLocationLabel,
    stockLocations,
    stockLocationId,
    rawStocks,
    customProduct,
    onCustomProductChange,
    allowCustom = false,
    placeholder,
    value,
    onChange,
  } = props;

  const { showProductDropdownMetadata } = usePreOrderFeatureFlags();
  const showProductMetadata =
    props.showProductMetadata ?? showProductDropdownMetadata;

  const { t } = useTranslation();
  const stockFilterMode = useFeatureFlag("recapStockFilterMode") ?? "persist";
  const state = useProductSelectState(props, stockFilterMode);

  // When the wrapper hydrates from a button click, open the dropdown so the
  // user's single click lands them in an interactive editor (matches the
  // pre-lazy behaviour). rAF defers to the next frame so refs settle first.
  const openOnMount = props.openOnMount;
  const focusSkuOnMount = props.focusSkuOnMount;
  const handleToggleRef = useRef(state.handleToggle);
  handleToggleRef.current = state.handleToggle;
  const skuInputRef = state.skuInputRef;
  useEffect(() => {
    if (!openOnMount) return;
    const raf = requestAnimationFrame(() => handleToggleRef.current());
    return () => cancelAnimationFrame(raf);
  }, [openOnMount]);
  // Tab-into-cell hydration path: the shell's SKU input lost focus during the
  // shell→editor swap. Refocus on the next frame so keyboard nav keeps
  // working — otherwise Tab would silently drop the user into nowhere.
  // Guard against stealing focus back if the user has already moved on
  // (Tab again, click) by the time the rAF fires.
  useEffect(() => {
    if (!focusSkuOnMount) return;
    const raf = requestAnimationFrame(() => {
      const active = document.activeElement;
      // Only refocus if focus has fallen to body/html (the shell unmount put
      // it there). If active is anywhere else, the user deliberately moved.
      if (
        active !== null &&
        active !== document.body &&
        active !== document.documentElement
      ) {
        return;
      }
      skuInputRef.current?.focus();
      skuInputRef.current?.select();
    });
    return () => cancelAnimationFrame(raf);
  }, [focusSkuOnMount, skuInputRef]);

  if (state.productsLoading) {
    return (
      <div className={className}>
        {label && (
          <label className="text-sm font-medium mb-2 block">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="flex gap-2">
          <div className="flex-1">
            <div className="px-3 py-2 border border-gray-200 rounded text-gray-500">
              Loading...
            </div>
          </div>
          <button disabled className="px-3 py-2 border border-gray-200 rounded">
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  const dropdownContent = state.isOpen && (
    <ProductDropdown
      isOpen={state.isOpen}
      dropdownPosition={state.dropdownPosition}
      openedByButton={state.openedByButton}
      searchTerm={state.searchTerm}
      isSearchPending={state.isSearchPending}
      setSearchTerm={state.setSearchTerm}
      setIsManuallyNavigating={state.setIsManuallyNavigating}
      setSelectedDropdownIndex={state.setSelectedDropdownIndex}
      handleKeyDown={state.handleKeyDown}
      allowCustom={allowCustom}
      isCustomActive={state.isCustomActive}
      setIsOpen={state.setIsOpen}
      setCustomDialogOpen={state.setCustomDialogOpen}
      isEnriched={state.isEnriched}
      priceLookup={priceLookup}
      stockLookup={state.internalStockLookup}
      stockLocationLabel={stockLocationLabel}
      stockLocations={stockLocations}
      localLocationId={state.localLocationId}
      setLocalLocationId={state.setLocalLocationId}
      dropdownColumns={state.dropdownColumns}
      filterInStock={state.filterInStock}
      setFilterInStock={state.setFilterInStock}
      filterPriced={state.filterPriced}
      setFilterPriced={state.setFilterPriced}
      filterCounts={state.filterCounts}
      setRenderPage={state.setRenderPage}
      showProductMetadata={showProductMetadata}
      currentProductDetails={state.currentProductDetails}
      value={value}
      selectedDropdownIndex={state.selectedDropdownIndex}
      productsToRender={state.productsToRender}
      productsLoading={state.productsLoading}
      filteredProducts={state.filteredProducts}
      hasMoreRenderPages={state.hasMoreRenderPages}
      skuInput={state.skuInput}
      getProductChildren={state.getProductChildren}
      onProductSelect={state.handleProductSelect}
      onSubproductSelect={state.handleSubproductSelect}
      hoveredProductId={state.hoveredProductId}
      setHoveredProductId={state.setHoveredProductId}
      submenuPosition={state.submenuPosition}
      setSubmenuPosition={state.setSubmenuPosition}
      allProducts={state.allProducts}
      allProductsWithChildren={state.allProductsWithChildren}
      dropdownRef={state.dropdownRef}
      searchInputRef={state.searchInputRef}
      observerTarget={state.observerTarget}
    />
  );

  return (
    <>
      <ProductDisplayValue
        value={value}
        disabled={disabled}
        isLastRow={isLastRow}
        isOpen={state.isOpen}
        isCustomActive={state.isCustomActive}
        currentProductDetails={state.currentProductDetails}
        customProduct={customProduct}
        placeholder={placeholder}
        priceLookup={priceLookup}
        hideClosedPricePill={props.hideClosedPricePill}
        stockLookup={stockLookup}
        stockSummary={state.stockSummary}
        skuInput={state.skuInput}
        className={className}
        label={label}
        required={required}
        handleToggle={state.handleToggle}
        handleButtonKeyDown={state.handleButtonKeyDown}
        handleKeyDown={state.handleKeyDown}
        handleSkuChange={state.handleSkuChange}
        handleSkuBlur={state.handleSkuBlur}
        handleSkuFocus={state.handleSkuFocus}
        setCustomDialogOpen={state.setCustomDialogOpen}
        buttonRef={state.buttonRef}
        skuInputRef={state.skuInputRef}
      />

      {typeof window !== "undefined" &&
        dropdownContent &&
        createPortal(dropdownContent, portalContainer || document.body)}

      {/* Custom entity dialog */}
      {allowCustom && (
        <CustomEntityDialog
          open={state.customDialogOpen}
          onOpenChange={state.setCustomDialogOpen}
          entity={customProduct || null}
          onSave={(entity) => {
            onCustomProductChange?.(entity);
            onChange(null, null, null);
            state.setCurrentProductDetails(null);
            state.setSkuInput("");
            state.setPreviousSkuValue("");
          }}
          title={t("products.customProduct", "Custom destination")}
        />
      )}
    </>
  );
};

/**
 * Lazy wrapper: keeps the partner-card open / add-pre-order paths cheap by
 * mounting `ProductSelectComboShell` first (zero `useProductSelectState`
 * cost, byte-identical closed markup) and hydrating the full editor on the
 * user's first gesture. A button-click hydration sets `openOnMount`, so a
 * single user click lands them in an open dropdown — matching the
 * pre-lazy UX, no double-click race.
 *
 * Disabled rows render the shell forever (they were never going to hydrate
 * the heavy state machine anyway). For the editable case the visual is
 * identical because the shell re-uses `ProductDisplayValue` with derived
 * `currentProductDetails` + `skuInput`; React reconciliation swaps the
 * component identity on hydration but the DOM is the same shape.
 */
type ActivationKind = "click" | "focus" | "hover";

const ProductSelectComboLazy = (props: ProductSelectComboProps) => {
  const [activation, setActivation] = useState<ActivationKind | null>(null);

  const activate = useCallback(() => {
    setActivation((prev) => prev ?? "focus");
  }, []);
  const activateOpen = useCallback(() => setActivation("click"), []);
  const activateHover = useCallback(() => {
    setActivation((prev) => prev ?? "hover");
  }, []);

  if (activation) {
    return (
      <ProductSelectComboInner
        {...props}
        openOnMount={activation === "click"}
        focusSkuOnMount={activation === "focus"}
      />
    );
  }
  // Hover-hydrate so by the time the user clicks, the full editor is mounted
  // and the click lands on the real button (no first-click race).
  return (
    <div onPointerEnter={activateHover}>
      <ProductSelectComboShell
        {...props}
        onActivate={activate}
        onActivateOpen={activateOpen}
      />
    </div>
  );
};

// Memoised so a parent re-render (e.g. a sibling cell editing) doesn't re-run
// hydration state. Callers must keep callback identities stable (see
// ProductCell's `useCallback` wrappers).
export const ProductSelectCombo = memo(ProductSelectComboLazy);
